import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, inject, output, ChangeDetectionStrategy } from '@angular/core';
import { customThemeQuartz } from '@app/ag-grid-custom-theme';
import { getMenuItems } from '@app/components/shared/grids/grid.config';
import { createDateValueFormatter } from '@app/components/shared/grids/grid-formatters.config';
import { LoadingSpinnerComponent } from '@components/shared/loading-spinner/loading-spinner.component';
import { GridService } from '@services/grid.service';
import { ReportColumnVisibilityService } from '@services/report-column-visibility.service';
import { ReportSingleService } from '@services/report-single.service';
import {
    currencyFormatterFromInput,
    decimalFormatter,
    formatArrayValue,
    formatBooleanYesNo,
    formatCustomPercentage,
    percentageFormatter
} from '@utils/format-functions';
import { AgGridModule } from 'ag-grid-angular';
import {
    ColDef,
    ColumnEvent,
    ColumnVisibleEvent,
    ExcelStyle,
    FirstDataRenderedEvent,
    GetMainMenuItemsParams,
    GridOptions,
    GridReadyEvent,
    themeQuartz
} from 'ag-grid-enterprise';
import { formatProbabilityRaw, getPercentageNumericValue } from './grid-format.utils';

@Component({
    selector: 'app-grid-tab',
    standalone: true,
    imports: [CommonModule, AgGridModule, LoadingSpinnerComponent],
    providers: [DatePipe],
    templateUrl: './grid-tab.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./grid-tab.component.scss']
})
export class GridTabComponent {
    visibilityChange = output<Record<string, boolean>>();

    private readonly service = inject(ReportSingleService);
    private readonly gridService = inject(GridService);
    private readonly reportColumnVisibilityService = inject(ReportColumnVisibilityService);
    private readonly datePipe = inject(DatePipe);
    private readonly dateValueFormatter = createDateValueFormatter(this.datePipe, 'MM-dd-yyyy');

    readonly isLoading = computed(() => this.service.isLoading());
    readonly gridData = computed(() => this.service.gridData());

    readonly columnDefs = computed(() => this.service.gridSettings());

    readonly layoutStyle = computed(() => this.service.configuration()?.gridTab?.layout ?? '');

    readonly theme = themeQuartz.withParams(customThemeQuartz);
    readonly defaultColDef: ColDef = { flex: 1, minWidth: 100 };
    readonly autoGroupColumnDef: ColDef = { minWidth: 200 };

    readonly excelStyles: ExcelStyle[] = [
        { id: 'dateFormat', dataType: 'DateTime', numberFormat: { format: 'mm/dd/yyyy' } },
        { id: 'currencyFormat', numberFormat: { format: '$#,##0.00' } },
        { id: 'percentageFormat', numberFormat: { format: '0.00%' } }
    ];

    readonly gridOptions = computed<GridOptions>(() => ({
        excelStyles: this.excelStyles,
        columnTypes: {
            percentage: {
                valueFormatter: (params: { value: number }) => percentageFormatter(params.value) ?? '',
                useValueFormatterForExport: false,
                cellClass: 'percentageFormat'
            },
            customPercentage: {
                valueFormatter: (params: { value: number }) => formatCustomPercentage(params.value) ?? '',
                useValueFormatterForExport: false,
                cellClass: 'percentageFormat'
            },
            percentageString: {
                valueFormatter: (params: { value?: unknown }) =>
                    formatProbabilityRaw(params.value as string | number | null | undefined),
                valueGetter: (params: { data?: Record<string, unknown>; column?: { getColId: () => string } }) => {
                    if (!params.data || !params.column) return '';
                    const rawValue = params.data[params.column.getColId()];
                    return getPercentageNumericValue(rawValue as string);
                },
                useValueFormatterForExport: true,
                cellClass: 'percentageFormat'
            },
            date: {
                valueFormatter: (params) => this.dateValueFormatter(params),
                useValueFormatterForExport: true,
                cellClass: 'dateFormat'
            },
            array: {
                valueFormatter: (params: { value: string[] | string }) => formatArrayValue(params.value),
                useValueFormatterForExport: true
            },
            currency: {
                valueFormatter: (params: { value: number | string }) => currencyFormatterFromInput(params.value),
                useValueFormatterForExport: true,
                cellClass: 'currencyFormat'
            },
            decimal2: {
                valueFormatter: (params: { value: number | string }) => decimalFormatter(params.value),
                useValueFormatterForExport: true
            },
            booleanYesNo: {
                valueFormatter: (params: { value: unknown }) => formatBooleanYesNo(params.value),
                useValueFormatterForExport: true
            }
        },
        defaultColDef: {
            sortable: true,
            comparator: (valueA: unknown, valueB: unknown) => {
                if (valueA == null && valueB == null) return 0;
                if (valueA == null) return -1;
                if (valueB == null) return 1;
                if (typeof valueA === 'number' && typeof valueB === 'number') {
                    return valueA === valueB ? 0 : valueA > valueB ? 1 : -1;
                }
                const a = typeof valueA === 'string' ? valueA : String(valueA);
                const b = typeof valueB === 'string' ? valueB : String(valueB);
                return a.toLowerCase().localeCompare(b.toLowerCase());
            },
            mainMenuItems: (params: GetMainMenuItemsParams) => getMenuItems(params, ['columnChooser'])
        },
        onSortChanged: () => this.gridService.getGridApi()?.ensureIndexVisible(0),
        enableCellTextSelection: true,
        suppressClipboardPaste: false,
        suppressContextMenu: true,
        stopEditingWhenCellsLoseFocus: true,
        cellSelection: true,
        processCellFromClipboard: (params: { value: string }) => params.value?.trim?.() ?? params.value
    }));

    onFirstDataRendered(params: FirstDataRenderedEvent): void {
        this.service.applySavedGridSettings();
        params.api.sizeColumnsToFit();
    }

    onGridReady(params: GridReadyEvent): void {
        this.gridService.removeGridApi();
        this.gridService.setGridApi(params.api);
        this.service.applySavedGridSettings();
    }

    onColumnVisibleChange(e: ColumnVisibleEvent): void {
        const change: Record<string, boolean> = {};
        if (e.column && e.visible !== undefined) {
            change[e.column.getColId()] = e.visible;
        } else if (e.columns?.length) {
            e.columns.forEach((col) => {
                change[col.getColId()] = col.isVisible();
            });
        }
        if (Object.keys(change).length) {
            this.service.ensureColumnVisibilityInitialized();
            this.reportColumnVisibilityService.syncFromGrid();
            this.service.onHiddenColumnsChanged();
        }
    }

    onColumnRowGroupChanged(event: ColumnEvent): void {
        const colsToRemove = event.columns?.filter((col) => !col.isRowGroupActive()).map((col) => col.getId());
        if (colsToRemove?.length) {
            this.gridService.clearSortFromCols(colsToRemove);
            this.gridService.setColumnsVisible(colsToRemove, true);
            this.service.ensureColumnVisibilityInitialized();
            this.reportColumnVisibilityService.syncFromGrid();
            this.service.onHiddenColumnsChanged();
        }
        this.gridService.limitGroupingColumns(3);
        const groupingState = this.gridService.getGroupingState();
        this.gridService.setGroupingStateValue(groupingState);
        this.service.onGroupingChanged(this.gridService.getColsNames(groupingState));
    }
}
